import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  CircularProgress,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  DirectionsCar as TripsIcon,
  PanTool as BrakeIcon,
  Timer as DurationIcon,
  AccessTime as SessionIcon,
  Speed as SpeedIcon,
  CheckCircle as ActiveIcon,
  Circle as InactiveIcon,
  ExpandMore as ExpandIcon,
  People as PassengersIcon
} from '@mui/icons-material';

const CartSummaryStats = ({ 
  data, 
  isLoading, 
  cartsList, 
  onCartFilterChange, 
  visibility = {}, 
  noWrapper = false 
}) => {
  const theme = useTheme();
  const [selectedCartFilter, setSelectedCartFilter] = useState('all_active');

  // Get date range from data if available
  const getDateRangeFromData = () => {
    if (data && data.timePeriod) {
      const startDate = new Date(data.timePeriod.startDate);
      const endDate = new Date(data.timePeriod.endDate);
      return [startDate, endDate];
    }
    return null;
  };

  const dateRange = getDateRangeFromData();

  // Handle cart filter change
  const handleCartFilterChange = (event) => {
    const newFilter = event.target.value;
    setSelectedCartFilter(newFilter);
    if (onCartFilterChange) {
      onCartFilterChange(newFilter);
    }
  };

  // Get cart filter options
  const getCartFilterOptions = () => {
    const options = [
      { 
        value: 'all_active', 
        label: 'All Active Carts', 
        icon: selectedCartFilter === 'all_active' ? <ActiveIcon color="success" /> : <TripsIcon color="primary" />
      }
    ];

    // Add individual carts by ID
    if (cartsList) {
      cartsList.forEach(cart => {
        const isSelected = selectedCartFilter === cart.id.toString();
        
        // Handle missing properties with fallback values
        const cartId = cart.id || '';
        const cartIsActive = cart.isActive || false;
        const cartName = cart.name || `Cart-${cart.id.toString().padStart(3, '0')}`;
        
        options.push({
          value: cart.id.toString(),
          label: cartName,
          icon: isSelected ? <ActiveIcon color="success" /> : <TripsIcon color="primary" />,
          subtitle: `cart number: ${cart.id.toString()}, (${cartIsActive ? "active" : "unactive"})`
        });
      });
    }

    return options;
  };

  // Get display label for selected filter
  const getSelectedLabel = () => {
    const options = getCartFilterOptions();
    const selected = options.find(opt => opt.value === selectedCartFilter);
    return selected ? selected.label : 'All Active Carts';
  };

  // Format time duration (convert minutes to readable format)
  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Format time (HH:MM format)
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  // Format speed
  const formatSpeed = (speed) => {
    if (!speed) return 'N/A';
    return `${speed.toFixed(1)} km/h`;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Icon 
            sx={{ 
              color: theme.palette[color].main, 
              mr: 1,
              fontSize: '1.5rem'
            }} 
          />
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Box>
        
        <Typography 
          variant="h4" 
          component="div" 
          sx={{ 
            fontWeight: 'bold',
            color: theme.palette[color].main,
            mb: 0.5
          }}
        >
          {isLoading ? <CircularProgress size={24} /> : value}
        </Typography>
        
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    const content = (
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          {!noWrapper && (
            <>
              <TripsIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Usage Summary Statistics
              </Typography>
            </>
          )}
          <Chip
            label="Loading..."
            size="small"
            color="primary"
            variant="outlined"
          />
          
          {/* Cart Filter Dropdown - disabled during loading */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={selectedCartFilter}
              onChange={handleCartFilterChange}
              disabled={true}
              displayEmpty
              variant="outlined"
              IconComponent={ExpandIcon}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  fontSize: '0.875rem',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: '16px',
                  color: theme.palette.grey[500],
                  fontWeight: 500,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ActiveIcon sx={{ fontSize: 16, color: theme.palette.grey[400] }} />
                  <span>{getSelectedLabel()}</span>
                </Box>
              )}
            >
              {getCartFilterOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={option.label}
                    secondary={option.subtitle}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </CardContent>
    );

    return noWrapper ? content : (
      <Card sx={{ mb: 4, boxShadow: 3 }}>
        {content}
      </Card>
    );
  }

  if (!data) {
    const content = (
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          {!noWrapper && (
            <>
              <TripsIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Usage Summary Statistics
              </Typography>
            </>
          )}
          <Chip
            label="No Data"
            size="small"
            color="warning"
            variant="outlined"
          />
          
          {/* Cart Filter Dropdown - enabled for retry */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={selectedCartFilter}
              onChange={handleCartFilterChange}
              displayEmpty
              variant="outlined"
              IconComponent={ExpandIcon}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  fontSize: '0.875rem',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.warning.main}`,
                  borderRadius: '16px',
                  color: theme.palette.warning.main,
                  fontWeight: 500,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ActiveIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                  <span>{getSelectedLabel()}</span>
                </Box>
              )}
            >
              {getCartFilterOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={option.label}
                    secondary={option.subtitle}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body1" color="text.secondary" align="center">
          No summary data available for the selected period.
        </Typography>
      </CardContent>
    );

    return noWrapper ? content : (
      <Card sx={{ mb: 4, boxShadow: 3 }}>
        {content}
      </Card>
    );
  }

  const content = (
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        {!noWrapper && (
          <>
            <TripsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Usage Summary Statistics
            </Typography>
          </>
        )}
        
        {dateRange && (
          <Chip
            label={`${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}

        {/* Cart Filter Dropdown */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={selectedCartFilter}
            onChange={handleCartFilterChange}
            displayEmpty
            variant="outlined"
            IconComponent={ExpandIcon}
            sx={{
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                fontSize: '0.875rem',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '16px',
                color: theme.palette.primary.main,
                fontWeight: 500,
                '&:focus': {
                  borderRadius: '16px',
                  backgroundColor: theme.palette.background.paper,
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                <span>{getSelectedLabel()}</span>
              </Box>
            )}
          >
            {getCartFilterOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  {option.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={option.label}
                  secondary={option.subtitle}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {visibility.dailyTrips !== false && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Daily Trips"
              value={data.avgTripsPerDay?.toFixed(1) || 'N/A'}
              subtitle="Average trips per day"
              icon={TripsIcon}
              color="primary"
            />
          </Grid>
        )}

        {visibility.estopBrake !== false && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="E-Stop Brake"
              value={data.avgManualEStopBrake?.toFixed(1) || 'N/A'}
              subtitle="Average manual e-stop per day"
              icon={BrakeIcon}
              color="error"
            />
          </Grid>
        )}

        {visibility.pedalBrake !== false && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Pedal Brake"
              value={data.avgManualPedalBrake?.toFixed(1) || 'N/A'}
              subtitle="Average manual pedal brake per day"
              icon={BrakeIcon}
              color="warning"
            />
          </Grid>
        )}

        {visibility.tripDuration !== false && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Trip Duration"
              value={formatDuration(data.avgTripDuration)}
              subtitle="Average trip duration"
              icon={DurationIcon}
              color="info"
            />
          </Grid>
        )}

        {visibility.dailyActiveTime !== false && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Daily active Time"
              value={formatDuration(data.avgDailySessionTime)}
              subtitle={`Avg start: ${formatTime(data.avgStartTime)} | Avg downtime: ${formatTime(data.avgDownTime)}`}
              icon={SessionIcon}
              color="success"
            />
          </Grid>
        )}

        {visibility.avgSpeed !== false && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Avg Speed"
              value={formatSpeed(data.avgTripSpeed)}
              subtitle="Average trip speed"
              icon={SpeedIcon}
              color="secondary"
            />
          </Grid>
        )}

        {visibility.avgPassengersCount !== false && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Avg Passengers"
              value={data.avgPassengersCount?.toFixed(1) || 'N/A'}
              subtitle="Average passengers per day"
              icon={PassengersIcon}
              color="info"
            />
          </Grid>
        )}
      </Grid>
    </CardContent>
  );

  return noWrapper ? content : (
    <Card sx={{ mb: 4, boxShadow: 3 }}>
      {content}
    </Card>
  );
};

export default CartSummaryStats;






